import { type LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";

// Haptic feedback utility
function hapticFeedback(duration: number = 10) {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    navigator.vibrate(duration);
  }
}

export interface BottomNavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  isActive?: boolean;
}

interface BottomNavProps {
  items: BottomNavItem[];
  onNavigate: (path: string) => void;
  className?: string;
  fabAction?: {
    icon: LucideIcon;
    label: string;
    onClick: () => void;
  };
}

export function BottomNav({ items, onNavigate, className, fabAction }: BottomNavProps) {
  // Split items for FAB placement in center
  const leftItems = fabAction ? items.slice(0, Math.ceil(items.length / 2)) : items;
  const rightItems = fabAction ? items.slice(Math.ceil(items.length / 2)) : [];

  const renderNavItem = (item: BottomNavItem) => {
    const Icon = item.icon;
    return (
      <button
        key={item.path}
        onClick={() => {
          hapticFeedback();
          onNavigate(item.path);
        }}
        className={cn(
          "group relative flex flex-1 flex-col items-center justify-center gap-1 transition-all duration-200",
          item.isActive ? "text-primary" : "text-muted-foreground"
        )}
      >
        {/* Active indicator */}
        <div
          className={cn(
            "absolute left-1/2 top-0 h-1 w-12 -translate-x-1/2 rounded-full transition-all duration-200",
            item.isActive ? "bg-primary" : "bg-transparent"
          )}
        />
        <div
          className={cn(
            "flex h-10 w-full max-w-16 items-center justify-center rounded-xl transition-all duration-200",
            item.isActive
              ? "bg-primary/10"
              : "bg-transparent group-hover:bg-muted group-active:scale-95"
          )}
        >
          <Icon
            className={cn("h-6 w-6 transition-all duration-200", item.isActive && "stroke-[2.5]")}
          />
        </div>
        <span
          className={cn(
            "text-xs font-medium transition-all duration-200",
            item.isActive && "font-semibold"
          )}
        >
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur-md lg:hidden",
        className
      )}
    >
      <div className="pb-safe flex h-20 items-stretch">
        {fabAction ? (
          <>
            {leftItems.map(renderNavItem)}
            {/* FAB in center */}
            <div className="relative flex flex-1 items-center justify-center">
              <button
                onClick={() => {
                  hapticFeedback(15);
                  fabAction.onClick();
                }}
                className="absolute -top-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl active:scale-95"
                aria-label={fabAction.label}
              >
                <fabAction.icon className="h-6 w-6" />
              </button>
            </div>
            {rightItems.map(renderNavItem)}
          </>
        ) : (
          items.map(renderNavItem)
        )}
      </div>
    </nav>
  );
}
